import dayjs from "dayjs";
import { AppNotificationContextState } from "./reducer";
import { Notification } from "../../../components/Layout/components/Header/components/Notifications/hooks/useNotifications/useNotifications";
import { CATEGORY_MAP } from "../../../pages/alertAndNotifications/list/constants";
import { dayJSLang } from "../../../types";
import {
  AlertsAndNotification,
  AlertsAndNotificationSource,
} from "../AppNotificationContext";

export interface NotificationsGridRow {
  id: string;
  eventId: string;
  isSummaryExpanded: boolean;
  type: string;
  date: string;
  createdAt: string;
  isRead: boolean;
  payload: object;
  dealerAccount: string;
}

export interface AlertsGridRow extends AlertsAndNotification {
  eventId: string;
  formattedEventType: string;
  formattedDate: string;
}

export function getAllAlerts(
  state: AppNotificationContextState
): AlertsAndNotification[] {
  const alerts = [
    ...state.appNotificationDomainAlerts.filter((alert) =>
      isAllowedAlert(alert, state)
    ),
  ];
  return sortByTimestamp(alerts);
}

function isAllowedAlert(
  event: AlertsAndNotification,
  state: AppNotificationContextState
) {
  const handleDomainAlertTypes: string[] = [
    "returns_daily_status_needaction",
    ...(isDailySurplusAlertAndNotificationEnabled(state)
      ? ["returns_daily_status_dealerpreparation"]
      : []),
    "claims_status_needaction",
    ...(isD2dOrderRequestNeedActionAlertEnabled(state)
      ? ["d2d_seller_order_requests"]
      : []),
    ...(isDailySurplusAlertAndNotificationEnabled(state)
      ? [
          "returns_surplus_status_needaction",
          "returns_surplus_status_waitingfornfe",
          "returns_surplus_status_dealerpreparation",
        ]
      : []),
  ];

  return (
    event.source === AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE &&
    handleDomainAlertTypes.includes(event.type)
  );
}

export function getAllNotifications(
  state: AppNotificationContextState
): AlertsAndNotification[] {
  const allNotifications = [
    ...state.downloadNotifications,
    ...state.appNotificationDomainNotifications.filter((notification) =>
      isHandledAppNotification(notification, state)
    ),
  ];
  return sortByTimestamp(allNotifications);
}

function isHandledAppNotification(
  event: AlertsAndNotification,
  state: AppNotificationContextState
) {
  const broadcastEnabled = isBroadcastMessageNotificationsEnabled(state);
  const handledAppNotificationTypes: string[] = [
    "orders_inventory_discrepancies",
    "order_creation",
    "returns_daily_status_completed",
    "returns_daily_status_rejected",
    "returns_daily_status_readytoship",
    "d2d_buyer_order_status_updates",
    "d2d_seller_order_requests",
    "d2d_order_status_accepted",
    "d2d_order_status_shipped",
    "claims_status_rejected",
    "claims_status_completed",
    ...(isDailySurplusAlertAndNotificationEnabled(state)
      ? ["returns_daily_status_created"]
      : []),
    ...(broadcastEnabled
      ? ["broadcast_message_created", "broadcast_message_updated"]
      : []),
    ...(isDailySurplusAlertAndNotificationEnabled(state)
      ? [
          "returns_surplus_status_created",
          "returns_surplus_status_readytoship",
          "returns_surplus_status_completed",
        ]
      : []),
  ];

  return handledAppNotificationTypes.includes(event.type);
}

function sortByTimestamp(
  alertsAndNotifications: AlertsAndNotification[]
): AlertsAndNotification[] {
  return alertsAndNotifications.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

export function getEventsLoaded(state: AppNotificationContextState): boolean {
  return state.eventsLoaded;
}

export function isRealTimeUpdatesDown(
  state: AppNotificationContextState
): boolean {
  return state.isRealTimeUpdatesDown;
}

export function hasDownloadNotificationsUpdatedFromStateValue(
  state: AppNotificationContextState,
  notificationsFromHook: Notification[]
) {
  if (state.downloadNotifications.length !== notificationsFromHook.length) {
    return true;
  }

  return notificationsFromHook.some((notificationFromHook) => {
    const matchingNotification = state.downloadNotifications.find(
      (notificationInState) =>
        notificationInState.id === notificationFromHook.payload.id
    );

    return (
      !matchingNotification ||
      matchingNotification.isRead !== notificationFromHook.isRead ||
      matchingNotification.payload.isExpired !== notificationFromHook.isExpired
    );
  });
}

export const hasUnreadNotifications = (
  notifications: AlertsAndNotification[]
): boolean => notifications.some((event) => !event.isRead);

function filterUnreadNotifications(notifications: AlertsAndNotification[]) {
  return notifications.filter((event) => !event.isRead);
}

function filterUnreadAlerts(alerts: AlertsAndNotification[]) {
  return alerts.filter((event) => !event.isRead);
}

export const getUnreadNotificationCount = (
  notifications: AlertsAndNotification[]
): number => filterUnreadNotifications(notifications).length;

export const hasUnreadAlerts = (alerts: AlertsAndNotification[]): boolean =>
  alerts.some((event) => !event.isRead);

export const getUnreadAlertCount = (alerts: AlertsAndNotification[]): number =>
  filterUnreadAlerts(alerts).length;

export const getUnreadAlerts = (alerts: AlertsAndNotification[]): Object[] =>
  filterUnreadAlerts(alerts).map((event) => event.payload);

export function filterAlertsToShowInHeader(alerts: AlertsAndNotification[]) {
  return alerts.filter(
    (event) =>
      event.source !== AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE ||
      !event.isRead
  );
}

export function filterNotificationsToShowInHeader(
  notifications: AlertsAndNotification[]
) {
  return notifications.filter(
    (event) =>
      event.source !== AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE ||
      !event.isRead
  );
}

export function filterAppNotificationDomainAlertsAndNotifications(
  alertsAndNotifications: AlertsAndNotification[]
) {
  return alertsAndNotifications.filter(
    (event) =>
      event.source === AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE
  );
}

export function getStatusCount(
  alerts: AlertsAndNotification[],
  notifications: AlertsAndNotification[]
) {
  const statusCount = {
    unread: 0,
    alerts: 0,
    notifications: 0,
    unreadAlert: 0,
    unreadNotification: 0,
  };
  const filteredAlerts =
    filterAppNotificationDomainAlertsAndNotifications(alerts);
  const filteredNotifications =
    filterAppNotificationDomainAlertsAndNotifications(notifications);

  filteredAlerts.forEach((alert) => {
    statusCount.alerts += 1;
    if (!alert.isRead) {
      statusCount.unreadAlert += 1;
      statusCount.unread += 1;
    }
  });
  filteredNotifications.forEach((notification) => {
    statusCount.notifications += 1;
    if (!notification.isRead) {
      statusCount.unreadNotification += 1;
      statusCount.unread += 1;
    }
  });
  return statusCount;
}

export function getGridRows(
  events: AlertsAndNotification[],
  language: string,
  allUnread: boolean
): NotificationsGridRow[] {
  if (allUnread)
    return events
      .filter((event) => !event.isRead)
      .map((event) => mapEvent(language, event));
  return events.map((event) => mapEvent(language, event));
}

function mapEvent(language: string, event: AlertsAndNotification) {
  const eventCategory = CATEGORY_MAP[event.type];
  return {
    id: event.id,
    eventId:
      event.source === AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE
        ? event.id.split("#")[0]
        : event.id,
    isRead: event.isRead,
    isSummaryExpanded: event?.isSummaryExpanded,
    type: eventCategory,
    payload: {
      orderNumber: event.payload.orderId ?? event.payload.orderNumber,
      returnId: event.payload.returnId,
      lineItems: event.payload.lineItems || [],
      claimId: event.payload.claimId,
      partNumber: event.payload.partNumber,
      msgId: event.payload.msgId,
      tag: event.payload.tag,
      title: event.payload.title,
      action: event.payload.action,
      ...(event.payload.surplusType
        ? { surplusType: event.payload.surplusType }
        : {}),
    },
    date: dayjs(event.createdAt)
      .locale(dayJSLang[language])
      .format("DD-MMM-YYYY"),
    createdAt: event.createdAt,
    source: event.source,
    dealerAccount: event.dealerId,
  };
}

export function mapAlertsToGridRows(
  alerts: AlertsAndNotification[],
  language: string,
  unreadOnly: boolean
): AlertsGridRow[] {
  const alertsToMap = unreadOnly
    ? alerts.filter((event) => !event.isRead)
    : alerts;
  return alertsToMap.map((event) => mapAlertEventForGrid(event, language));
}

function mapAlertEventForGrid(
  alert: AlertsAndNotification,
  language: string
): AlertsGridRow {
  return {
    ...alert,
    eventId:
      alert.source === AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE
        ? alert.id.split("#")[0]
        : alert.id,
    formattedEventType: CATEGORY_MAP[alert.type],
    formattedDate: dayjs(alert.createdAt)
      .locale(dayJSLang[language])
      .format("DD-MMM-YYYY"),
  };
}

function isDailySurplusAlertAndNotificationEnabled(
  state: AppNotificationContextState
) {
  return state.toggles.isDailySurplusAlertAndNotificationEnabled;
}

function isD2dOrderRequestNeedActionAlertEnabled(
  state: AppNotificationContextState
) {
  return state.toggles.isD2dOrderRequestNeedActionAlertEnabled;
}

function isBroadcastMessageNotificationsEnabled(
  state: AppNotificationContextState
) {
  return state.toggles.isBroadcastMessageNotificationsEnabled;
}
